import React, { useState, useEffect, useCallback } from 'react';
import { Button, Upload } from 'antd';
import Spinner from '../../components/elements/Spinner/Spinner';
import { toast } from 'react-toastify';
import ContentLayout from '../../layout/ContentLayout';
import { FloatingLabelInput } from '../../components/elements/floatingLabelInput/FloatingLabelInput';
import RichTextEditor from '../../components/elements/RichTextEditor/RichTextEditor';
import mobileInfoPageService from '../../api/mobile-info-page-api';
import TranslateButton from '../../components/elements/TranslateButton/TranslateButton';
import { useTranslateFields } from '../../hooks/useTranslateFields';
import styles from '../../styles/mobileInfoPage.module.scss';
import annStyles from '../../styles/editAnnoucement.module.scss';

const LANG_SECTIONS = [
  { key: 'sr', label: 'Srpski' },
  { key: 'hu', label: 'Mađarski' },
  { key: 'en', label: 'Engleski' },
];

const emptyForm = {
  title: { sr: '', hu: '', en: '' },
  text: { sr: '', hu: '', en: '' },
  legalText: { sr: '', hu: '', en: '' },
  images: [],
};

export default function MobilniInfoPage() {
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    const result = await mobileInfoPageService.getForAdmin();
    if (result.success && result.data) {
      setFormData({
        title: result.data.title || { sr: '', hu: '', en: '' },
        text: result.data.text || { sr: '', hu: '', en: '' },
        legalText: result.data.legalText || { sr: '', hu: '', en: '' },
        images: result.data.images || [],
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const updateTitle = (lang, value) => {
    setFormData((prev) => ({
      ...prev,
      title: { ...prev.title, [lang]: value },
    }));
  };

  const updateText = (lang, value) => {
    setFormData((prev) => ({
      ...prev,
      text: { ...prev.text, [lang]: value },
    }));
  };

  const updateLegalText = (lang, value) => {
    setFormData((prev) => ({
      ...prev,
      legalText: { ...prev.legalText, [lang]: value },
    }));
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const handleAddImage = async (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Dozvoljen je samo JPG, PNG ili WebP format');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Slika je prevelika. Maksimalna veličina je 5MB');
      return false;
    }
    try {
      const base64 = await fileToBase64(file);
      setFormData((prev) => ({ ...prev, images: [...prev.images, base64] }));
    } catch {
      toast.error('Greška pri učitavanju slike');
    }
    return false;
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const validate = () => {
    let valid = true;

    if (!formData.title.sr || !formData.title.hu || !formData.title.en) {
      toast.error('Naslov mora biti popunjen na svim jezicima.');
      valid = false;
    }
    if (!formData.text.sr || !formData.text.hu || !formData.text.en) {
      toast.error('Tekst mora biti popunjen na svim jezicima.');
      valid = false;
    }

    return valid;
  };

  const { translating, translate } = useTranslateFields();

  const handleTranslate = async () => {
    if (!formData.title.sr?.trim() || !formData.text.sr?.trim()) {
      toast.error('Popunite naslov i tekst na srpskom pre prevođenja');
      return;
    }

    const fields = { title: formData.title.sr, text: formData.text.sr };
    const htmlFields = ['text'];
    if (formData.legalText.sr?.trim()) {
      fields.legalText = formData.legalText.sr;
      htmlFields.push('legalText');
    }

    const result = await translate(fields, htmlFields);
    if (!result) return;

    for (const lang of ['hu', 'en']) {
      updateTitle(lang, result[lang].title);
      updateText(lang, result[lang].text);
      if (result[lang].legalText !== undefined) {
        updateLegalText(lang, result[lang].legalText);
      }
    }

    toast.success('Prevod je uspešno primenjen');
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    const result = await mobileInfoPageService.update(formData);
    if (result.success) {
      toast.success('Stranica je uspešno sačuvana');
      if (result.data) {
        setFormData({
          title: result.data.title || { sr: '', hu: '', en: '' },
          text: result.data.text || { sr: '', hu: '', en: '' },
          legalText: result.data.legalText || { sr: '', hu: '', en: '' },
          images: result.data.images || [],
        });
      }
    } else {
      toast.error(result.message || 'Došlo je do greške');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <ContentLayout>
        <Spinner size="lg" />
      </ContentLayout>
    );
  }

  return (
    <ContentLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 className={styles.pageTitle}>Mobilni info</h2>
        <TranslateButton onClick={handleTranslate} loading={translating} />
      </div>

      {/* Title sections */}
      {LANG_SECTIONS.map(({ key, label }) => (
        <section key={`title-${key}`} className={styles.section}>
          <h3 className={styles.sectionTitle}>Naslov — {label}</h3>
          <FloatingLabelInput
            label={`Naslov (${label})`}
            value={formData.title[key]}
            onChange={(e) => updateTitle(key, e.target.value)}
            placeholder={`Unesite naslov na ${label.toLowerCase()}om`}
          />
        </section>
      ))}

      {/* Text sections */}
      {LANG_SECTIONS.map(({ key, label }) => (
        <section key={`text-${key}`} className={styles.section}>
          <h3 className={styles.sectionTitle}>Tekst — {label}</h3>
          <RichTextEditor
            value={formData.text[key]}
            onChange={(val) => updateText(key, val)}
            placeholder={`Unesite tekst na ${label.toLowerCase()}om`}
          />
        </section>
      ))}

      {/* Legal text sections */}
      {LANG_SECTIONS.map(({ key, label }) => (
        <section key={`legal-${key}`} className={styles.section}>
          <h3 className={styles.sectionTitle}>Pravni tekst — {label}</h3>
          <RichTextEditor
            value={formData.legalText[key]}
            onChange={(val) => updateLegalText(key, val)}
            placeholder={`Unesite pravni tekst na ${label.toLowerCase()}om`}
          />
        </section>
      ))}

      {/* Images */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Slike</h3>
        <div className={styles.imagesRow}>
          {formData.images.map((src, index) => (
            <div key={index} className={annStyles.previewWrapper}>
              <img src={src} alt={`Slika ${index + 1}`} className={annStyles.imagePreview} />
              <button
                type="button"
                className={annStyles.deleteBtn}
                onClick={() => handleRemoveImage(index)}
              >
                <span className={annStyles.trashIcon}>🗑️</span>
              </button>
            </div>
          ))}

          <Upload
            accept="image/jpeg,image/jpg,image/png,image/webp"
            beforeUpload={handleAddImage}
            showUploadList={false}
            multiple
            className={styles.uploadWrapper}
          >
            <div className={annStyles.uploadBox}>
              <div className={annStyles.uploadContent}>
                <div className={annStyles.cloudIconContainer}>
                  <span className={annStyles.cloudIcon}>☁️</span>
                </div>
                <p className={annStyles.uploadText}>
                  <span className={annStyles.highlight}>Kliknite za otpremanje</span> ili prevuci i ispusti
                </p>
                <small className={annStyles.fileTypes}>.JPG, .PNG, .WebP (max. 5MB)</small>
              </div>
            </div>
          </Upload>
        </div>
      </section>

      <div className={styles.bottomAction}>
        <Button
          className="btn-primary"
          onClick={handleSave}
          loading={saving}
        >
          Sačuvaj
        </Button>
      </div>
    </ContentLayout>
  );
}
